import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { Alert, Modal, Table } from 'antd'
import type { TableProps } from 'antd/es/table'

export const AlertSettingsCopyToModal = ({
  open,
  availableProjects,
  onClose,
  onCopyTo
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [showWarning, setShowWarning] = useState(false)

  const alertUsageByProject = useSelector((state: any) => {
    return state.alertRules.alertUsageByProject
  })

  const handleCopyTo = () => {
    onCopyTo(selectedRowKeys)
    handleOnClose()
  }

  const handleOnClose = () => {
    onClose()
    clearSelection()
  }

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys)
  }

  const clearSelection = () => {
    setSelectedRowKeys([])
  }

  type TableRowSelection<T extends object = object> =
    TableProps<T>['rowSelection']

  const rowSelection: TableRowSelection<any> = {
    selectedRowKeys,
    onChange: onSelectChange,
    selections: [
      {
        key: 'selectAll',
        text: 'Select all',
        onSelect: changeableRowKeys => {
          setSelectedRowKeys(changeableRowKeys)
        }
      },
      {
        key: 'deselectAll',
        text: 'Deselect all',
        onSelect: clearSelection
      }
    ],
    columnWidth: '3.75rem',
    getCheckboxProps: record => ({
      disabled: record.status === 'Closed' || record.disabled
    })
  }

  // If one or more selected projects have Active Alerts > 0, show a warning notification
  useEffect(() => {
    if (selectedRowKeys.length === 0) {
      setShowWarning(false)
      return
    }

    const projectsWithAlerts = selectedRowKeys.filter(projectId => {
      const usage = alertUsageByProject
        ? alertUsageByProject[projectId as string]?.count
        : 0
      return usage > 0
    })

    if (projectsWithAlerts.length > 0) {
      setShowWarning(true)
    } else {
      setShowWarning(false)
    }
  }, [selectedRowKeys, alertUsageByProject])

  return (
    <Modal
      title="Copy alert settings to other projects"
      open={open}
      onCancel={handleOnClose}
      okText="Copy to selected projects"
      okButtonProps={{
        disabled: selectedRowKeys.length < 1
      }}
      onOk={handleCopyTo}
    >
      <Table
        rowKey={row => row.key}
        rowClassName={row =>
          row.status === 'Closed' || row.disabled ? 'disabled-row' : ''
        }
        rowSelection={rowSelection}
        columns={[
          {
            title: 'Project',
            dataIndex: 'label',
            key: 'label'
          },
          {
            title: 'Active alerts',
            align: 'center',
            width: '6.5rem',
            render: (_text, record) => {
              const usage = alertUsageByProject
                ? alertUsageByProject[record.key as string]?.count
                : 0
              return <span>{usage}</span>
            }
          }
        ]}
        dataSource={availableProjects}
        size={'small'}
        scroll={availableProjects.length > 8 ? { y: '312px' } : undefined}
        sticky
        pagination={false}
      />
      {showWarning && (
        <Alert
          message="One or more selected projects already have alert settings configured."
          description="Copying settings will overwrite these existing configurations."
          type="warning"
          style={{ marginTop: '1rem', padding: '0.5rem 0.75rem' }}
        />
      )}
    </Modal>
  )
}
